import React, { useState, useEffect, useRef } from 'react';
import Modal from 'react-modal';
import { FlexContainer } from '@/components/containers/FlexContainer/FlexContainer';
import { TextField, InputAdornment, MenuItem } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import Label from '@/components/form/Label/Label';
import ReactPixel from 'react-facebook-pixel';
import TuneIcon from '@mui/icons-material/Tune';
import CheckIcon from '@mui/icons-material/Check';
import { format, addDays } from 'date-fns';
import { setSearchFormData } from '@/redux/actions/searchFormDataActions';
import { staticLocationsFromSearch } from '@/helpers/locationHelpers';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

const fetchUserLocation = async () => {
  try {
    const response = await fetch('http://ip-api.com/json/');
    if (!response.ok) throw new Error(`Error: ${response.status}`);
    const data = await response.json();
    return { lat: data.lat, lng: data.lon };
  } catch (error) {
    console.error('Error fetching location:', error);
    return null;
  }
};

const validationSchema = Yup.object().shape({
  location: Yup.string().required('El lugar es obligatorio'),
  startDate: Yup.date().required('La fecha es obligatoria'),
  //endTime: Yup.string().required('El horario es obligatorio'),
  guests: Yup.number()
    .min(1, 'Debe haber al menos 1 persona')
    .required('Cantidad de personas requerida'),
});

const NewGeneralSearchForm = (props) => {
  const { searchType } = props;
  const [userLocation, setUserLocation] = useState(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [coordinates, setCoordinates] = useState({ lat: null, lng: null });
  const [customDateError, setCustomDateError] = useState('');

  const autocompleteService = useRef(null);
  const tomorrow = format(addDays(new Date(), 1), 'yyyy-MM-dd');
  const lenguage = useSelector((state) => state.lenguage?.lang);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const setSelectedSuggestionFromGoogleService = (value) => {
    setSelectedSuggestion(value);
    if (value?.place_id && window.google) {
      const service = new window.google.maps.places.PlacesService(document.createElement('div'));
      service.getDetails({ placeId: value?.place_id }, (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          const newCoordinates = place.geometry.location.toJSON();
          setCoordinates(newCoordinates);
        }
      });
    }
  };

  useEffect(() => {
    const initializeAutocomplete = () => {
      if (!autocompleteService.current && window.google) {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
      }
    };

    fetchUserLocation().then((location) => setUserLocation(location));

    if (!window.google) {
      window.addEventListener('load', initializeAutocomplete);
      return () => window.removeEventListener('load', initializeAutocomplete);
    } else {
      initializeAutocomplete();
      setSelectedSuggestionFromGoogleService(
        lenguage === 'ES' ? staticLocationsFromSearch[1] : staticLocationsFromSearch[0],
      );
    }
  }, []);

  const initialValues = {
    location:
      lenguage === 'ES'
        ? staticLocationsFromSearch[1]?.description
        : staticLocationsFromSearch[0]?.description_en,
    startDate: tomorrow,
    startTime: '10:00',
    endTime: '11:00',
    guests: 1,
  };

  const handleSubmit = (values) => {
    const spaceType = searchType === 'Sala' ? 'sala de juntas' : 'coworking';
    const searchFormData = {
      ...values,
      coordinates,
      selectedSuggestion,
      spaceType,
    };
    dispatch(setSearchFormData(searchFormData));
    ReactPixel.trackCustom('submitSearchForm', { clave: searchFormData });
    navigate(lenguage === 'ES' ? '/lista-espacios' : '/spaces-list');
  };

  return (
    <FlexContainer className={'flex-column'}>
      <FlexContainer className={'format100 '}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, setFieldValue }) => {
            const setSelectedSuggestionxCBO = (value) => {
              setSelectedSuggestionFromGoogleService(value);
              setFieldValue('location', value?.description);
            };

            return (
              <Form className={'width100'}>
                <FlexContainer className={'flex-column width100 justify between gap-10'}>
                  <FlexContainer className={'width100 justify between gap-10'}>
                    <TextField
                      className={'bgBlueGradient border-rad10px'}
                      fullWidth
                      select
                      label={lenguage === 'ES' ? 'Donde' : 'Where'}
                      value={values.location}
                      onChange={(e) => setFieldValue('location', e.target.value)}
                      SelectProps={{
                        renderValue: (selected) => {
                          const selectedOption = staticLocationsFromSearch?.find(
                            (s) => s.description === selected,
                          );
                          return selectedOption
                            ? lenguage === 'ES'
                              ? selectedOption.description
                              : selectedOption.description_en
                            : '';
                        },
                      }}
                      InputLabelProps={{
                        shrink: true,
                        sx: {
                          paddingTop: '18px',
                          paddingLeft: '44px',
                          color: '#ffffff',
                          fontWeight: 600,
                        },
                      }}
                      InputProps={{
                        notched: false,
                        sx: {
                          fontSize: '12px',
                          fontWeight: '600',
                          color: '#ffffff',
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(129, 146, 192, 0.4)',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(129, 146, 192, 0.4)',
                          },
                          '& .MuiOutlinedInput-input': {
                            paddingRight: '32px',
                            paddingTop: '26px',
                            paddingBottom: '8px',
                          },
                        },
                      }}
                    >
                      <MenuItem value="" disabled sx={{ fontSize: '10px' }}>
                        {lenguage === 'ES' ? 'Selecciona un lugar' : 'Select a location'}
                      </MenuItem>

                      {staticLocationsFromSearch?.map((suggestion) => (
                        <MenuItem
                          onClick={() => setSelectedSuggestionxCBO(suggestion)}
                          key={suggestion?.place_id}
                          value={suggestion?.description}
                          sx={{ fontSize: '14px', display: 'flex', alignItems: 'center' }}
                        >
                          <img
                            src={suggestion?.icon}
                            alt="icon"
                            style={{
                              width: '40px',
                              height: '40px',
                              marginRight: '8px',
                              objectFit: 'contain',
                            }}
                          />
                          {suggestion?.description}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      className={'bgBlueGradient border-rad10px'}
                      fullWidth
                      type="date"
                      label={lenguage === 'ES' ? 'Cuando' : 'When'}
                      value={values.startDate}
                      error={customDateError !== ''}
                      onClick={(e) => {
                        const input = e.currentTarget.querySelector('input');
                        if (input) {
                          try {
                            input.showPicker?.();
                          } catch (err) {
                            input.focus();
                          }
                        }
                      }}
                      onChange={(e) => {
                        const selectedDate = e.target.value;
                        if (selectedDate >= tomorrow) {
                          setFieldValue('startDate', selectedDate);
                          setCustomDateError('');
                        } else {
                          setCustomDateError('Debe ingresar una fecha posterior al día actual');
                        }
                      }}
                      autoFocus
                      InputLabelProps={{
                        shrink: true,
                        sx: {
                          locale: 'es-ES',
                          paddingTop: '18px',
                          paddingLeft: '44px',
                          color: '#ffffff',
                          fontWeight: 600,
                        },
                      }}
                      InputProps={{
                        inputProps: {
                          min: tomorrow,
                          pattern: '[0-9]{4}-[0-9]{2}-[0-9]{2}',
                          style: {
                            WebkitAppearance: 'none',
                            MozAppearance: 'textfield',
                          },
                        },
                        notched: false,
                        sx: {
                          height: '56px',
                          fontSize: '12px',
                          fontWeight: '600',
                          color: '#ffffff',
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                          '& .MuiOutlinedInput-input': {
                            border: 'none',
                            paddingRight: '32px',
                            paddingTop: '26px',
                            paddingBottom: '8px',
                            cursor: 'pointer',
                            '::-webkit-calendar-picker-indicator': {
                              display: 'none',
                              WebkitAppearance: 'none',
                            },
                          },
                        },
                      }}
                    ></TextField>
                  </FlexContainer>
                  <FlexContainer className={'width100 justify between'}>
                    <button
                      className={
                        'bgBlueGradient border-rad8px borderNone fontWhite grayShadow16 m-format100 padtb10 text-14'
                      }
                      type="submit"
                    >
                      <strong>{lenguage === 'ES' ? 'Pase de Coworking' : '1 Day Pass'}</strong>
                    </button>
                  </FlexContainer>
                </FlexContainer>
              </Form>
            );
          }}
        </Formik>
      </FlexContainer>
    </FlexContainer>
  );
};

const NewMobileSearchBar = (props) => {
  const { searchType, openModal } = props;
  const [userLocation, setUserLocation] = useState(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [coordinates, setCoordinates] = useState({ lat: null, lng: null });
  const [customDateError, setCustomDateError] = useState('');

  const autocompleteService = useRef(null);
  const tomorrow = format(addDays(new Date(), 1), 'yyyy-MM-dd');
  const lenguage = useSelector((state) => state.lenguage?.lang);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const setSelectedSuggestionFromGoogleService = (value) => {
    setSelectedSuggestion(value);
    if (value?.place_id && window.google) {
      const service = new window.google.maps.places.PlacesService(document.createElement('div'));
      service.getDetails({ placeId: value?.place_id }, (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          const newCoordinates = place.geometry.location.toJSON();
          setCoordinates(newCoordinates);
        }
      });
    }
  };

  useEffect(() => {
    const initializeAutocomplete = () => {
      if (!autocompleteService.current && window.google) {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
      }
    };

    fetchUserLocation().then((location) => setUserLocation(location));

    if (!window.google) {
      window.addEventListener('load', initializeAutocomplete);
      return () => window.removeEventListener('load', initializeAutocomplete);
    } else {
      initializeAutocomplete();
      setSelectedSuggestionFromGoogleService(
        lenguage === 'ES' ? staticLocationsFromSearch[1] : staticLocationsFromSearch[0],
      );
    }
  }, []);

  const initialValues = {
    location:
      lenguage === 'ES'
        ? staticLocationsFromSearch[1]?.description
        : staticLocationsFromSearch[0]?.description_en,
    startDate: tomorrow,
    startTime: '10:00',
    endTime: '11:00',
    guests: 1,
  };

  const handleSubmit = (values) => {
    const spaceType = searchType === 'Sala' ? 'sala de juntas' : 'coworking';
    const searchFormData = {
      ...values,
      coordinates,
      selectedSuggestion,
      spaceType,
    };
    dispatch(setSearchFormData(searchFormData));
    ReactPixel.trackCustom('submitSearchForm', { clave: searchFormData });
    navigate(lenguage === 'ES' ? '/lista-espacios' : '/spaces-list');
  };

  return (
    <FlexContainer className={'flex-column'}>
      <FlexContainer className={'format100 '}>
        <FlexContainer className={'flex-column width100 justify between gap-6'}>
          <FlexContainer className={'width100 justify between gap-6'}>
            <FlexContainer
              className={
                'flex-column width100 bgBlueGradient border-rad10px justify between padtb10'
              }
              fullWidth
              onClick={openModal}
            >
              <Label className={'fontWhite text-14 text-center'} fullWidth>
                {lenguage === 'ES' ? 'Donde' : 'Where'}
              </Label>
              <Label className={'fontWhite text-14 text-center'} fullWidth>
                <strong>{initialValues?.location?.substring(0, 15) + '...'}</strong>
              </Label>
            </FlexContainer>
            <FlexContainer
              className={
                'flex-column width100 bgBlueGradient border-rad10px justify between padtb10'
              }
              fullWidth
              onClick={openModal}
            >
              <Label className={'fontWhite text-14 text-center'} fullWidth>
                {lenguage === 'ES' ? 'Cuando' : 'When'}
              </Label>
              <Label className={'fontWhite text-14 text-center'} fullWidth>
                <strong>{initialValues?.startDate}</strong>
              </Label>
            </FlexContainer>
          </FlexContainer>
          <FlexContainer className={'width100 justify between'}>
            <button
              className={
                'bgBlueGradient border-rad8px borderNone fontWhite grayShadow16 m-format100 padtb10 text-14 flex items-center justify-between gap-2'
              }
              type="submit"
            >
              <strong>{lenguage === 'ES' ? ' Pase de Coworking' : ' 1 Day Pass'}</strong>
              <TuneIcon style={{ fontSize: 18, color: 'white' }} className={'fright'} />
            </button>
          </FlexContainer>
        </FlexContainer>
      </FlexContainer>
    </FlexContainer>
  );
};

const customStyles = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(2px)',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '1rem',
  },
  content: {
    position: 'relative',
    inset: 'auto',
    marginTop: '2rem',
    border: 'none',
    borderRadius: '16px',
    padding: '16px 18px',
    background: '#fff',
    minWidth: '100%',
    width: '100%',
    maxHeight: '85vh',
    minHeight: '100%',
    overflow: 'auto',
    boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
  },
};

const Pill = ({ active, label, hint, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={
        'flex-container border-rad100px margb4 padtb8 ' +
        (active ? 'bgPurple400 borderNone' : 'bgWhite borderPink margb4')
      }
    >
      {active && (
        <span className="flex-1 text-left fontWhite text-16 margl10">
          {'  '}
          {label}{' '}
          <CheckIcon style={{ fontSize: 18, color: 'white' }} className={'fright margr10'} />
        </span>
      )}
      {!active && (
        <span className="flex-1 text-left fontGray500 text-16 margl10">
          {'  '}
          {label}{' '}
        </span>
      )}
    </button>
  );
};

const options = [
  {
    title: 'Solo Workspaces',
    items: [
      { id: 'day-pass', label: 'Day Pass' },
      // { id: 'by-hour', label: 'By the Hour' },
    ],
  },
  {
    title: 'Team Rooms',
    items: [
      { id: 'small', label: 'Small Room', hint: ' (2–4 People)' },
      { id: 'medium', label: 'Medium Room', hint: ' (Up to 8 People)' },
      { id: 'large', label: 'Large Room', hint: ' (Up to 15 People)' },
    ],
  },
];

const WorkspacesModal = ({ isOpen, onClose, defaultValue = 'day-pass', onSelect, closeModal }) => {
  const [value, setValue] = useState(defaultValue);

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} style={customStyles} contentLabel="Modal">
      {/* Cerrar */}
      <button
        className={'bgWhite  fright borderNone fontGray500 text-20 fontBlack'}
        onClick={closeModal}
      >
        x
      </button>

      <FlexContainer className="flex-column gap-60">
        {options.map((block) => (
          <FlexContainer className="flex-column margb10" key={block.title}>
            <FlexContainer
              className="margb10"
              style={{
                color: '#1f262f',
                fontWeight: 700,
                fontSize: 18,
                paddingLeft: 4,
              }}
            >
              {block.title}
            </FlexContainer>

            <FlexContainer className="flex-column margl10">
              {block.items.map((opt) => (
                <Pill
                  key={opt.id}
                  label={opt.label}
                  hint={opt.hint}
                  active={value === opt.id}
                  onClick={() => {
                    setValue(opt.id);
                    onSelect?.(opt.id);
                  }}
                />
              ))}
            </FlexContainer>
          </FlexContainer>
        ))}
      </FlexContainer>
    </Modal>
  );
};

const NewHeaderSearchComponent = () => {
  const [showHeader, setShowHeader] = useState(true);
  const [modalIsOpen, setIsOpen] = useState(false);

  const openModal = () => {
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowHeader(window.scrollY < 220);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!showHeader) return null;
  return (
    <>
      <FlexContainer
        style={{ zIndex: 1, left: '5%', top: '43px' }}
        className={
          'FlexContainer border-rad8px flex-column flex-container d-hide m-format90 padtb8 pos-fixed'
        }
      >
        <NewMobileSearchBar searchType={'Sala'} openModal={openModal} />
      </FlexContainer>
      <WorkspacesModal
        isOpen={modalIsOpen}
        onClose={() => closeModal()}
        defaultValue="day-pass"
        onSelect={(val) => console.log('selected:', val)}
        closeModal={closeModal}
      />
    </>
  );
};

export default NewHeaderSearchComponent;
